/**
 * src/lib/api-client.ts
 *
 * Base HTTP client for Ona24 backend.
 *   - Auth is handled entirely via httpOnly cookies (set by the backend).
 *   - No tokens are read, stored, or attached by this client — the browser
 *     sends the cookie automatically on every request via `credentials: 'include'`.
 *   - Handles 401 → auto-refresh (cookie-based) → retry once
 *   - Throws ApiError with status + detail so callers handle cleanly
 */

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8000/api/v1';

// ── Typed error ───────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

// ── Query params type ───────────────────────────────────────────────────

export type QueryParams = {
  [key: string]: string | number | boolean | null | undefined;
};

// ── Public routes that should never be forced to /login ────────────────
// Any route reachable without a session (marketing/legal pages, etc.)
// needs to be listed here, or a background auth check will bounce
// logged-out visitors away from it.
const PUBLIC_PATHS = ['/login', '/privacy', '/terms', '/account-deletion', '/download'];

// ── Endpoints that must NEVER trigger the refresh-and-retry flow ───────────
// A 401 from any of these means "bad credentials" or "no session yet" —
// not "access token expired mid-session". If we let the refresh flow run
// here, it fires a doomed refresh call and overwrites the real backend
// message (e.g. "Invalid email or password.") with a generic
// "Session expired" — which is exactly the bug we're fixing.
//
// Matches by substring so it covers every login variant your backend
// exposes (/auth/web/login, /auth/mobile/login, /auth/mobile/login-phone)
// plus /auth/refresh itself, without having to keep an exact-path list
// in sync.
const NO_REFRESH_RETRY_MATCHERS = ['/login', '/refresh', '/register'];

function isAuthEndpoint(path: string): boolean {
  return NO_REFRESH_RETRY_MATCHERS.some((m) => path.includes(m));
}

// ── Internal: deduplicated refresh ────────────────────────────────────────

let _refreshPromise: Promise<void> | null = null;

async function refreshAccessToken(): Promise<void> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      throw new ApiError(401, 'Session expired — please log in again.');
    }
    // New access_token cookie is set by the Set-Cookie header on this response.
    // Nothing to store client-side.
  })().finally(() => { _refreshPromise = null; });

  return _refreshPromise;
}

// ── Core request ──────────────────────────────────────────────────────────

interface RequestOptions extends RequestInit {
  params?: QueryParams;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  _isRetry = false,
): Promise<T> {
  const { params, ...init } = options;

  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, String(v));
      }
    });
  }

  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(url.toString(), {
    ...init,
    headers,
    credentials: 'include',
  });

  // Only attempt the refresh-and-retry dance for requests that were made
  // against an *already-authenticated* endpoint. Login/refresh/register
  // returning 401 means "bad credentials", not "expired token" — so we
  // fall through to the normal error-parsing block below instead, which
  // surfaces the real backend `detail` (e.g. "Invalid email or password.").
  if (res.status === 401 && !_isRetry && !isAuthEndpoint(path)) {
    try {
      await refreshAccessToken();
      return request<T>(path, options, true);
    } catch {
      if (
        typeof window !== 'undefined' &&
        !PUBLIC_PATHS.includes(window.location.pathname)
      ) {
        window.location.href = '/login';
      }
      throw new ApiError(401, 'Session expired');
    }
  }

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      const raw = body?.detail;
      if (Array.isArray(raw)) {
        detail = raw[0]?.msg ?? detail;
      } else if (typeof raw === 'string') {
        detail = raw;
      }
    } catch { /* non-JSON */ }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

// ── Convenience methods ───────────────────────────────────────────────────

export const apiClient = {
  get:    <T>(path: string, params?: QueryParams) =>
    request<T>(path, { method: 'GET', params }),
  post:   <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  patch:  <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
};